import i18next, { type InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import {
  da,
  de,
  enGB,
  es,
  fi,
  fr,
  it,
  nb,
  nl,
  pl,
  pt,
  sv,
  tr,
} from 'date-fns/locale';
import { api } from '@http/constants';
import { I18N_EVENTS } from '@/i18n/events';

export const languages = {
  de: {
    name: 'Deutsch',
    locale: de,
  },
  en: {
    name: 'English',
    locale: enGB,
  },
  es: {
    name: 'Español',
    locale: es,
  },
  fr: {
    name: 'Français',
    locale: fr,
  },
  it: {
    name: 'Italiano',
    locale: it,
  },
  nl: {
    name: 'Dutch',
    locale: nl,
  },
  pl: {
    name: 'Polski',
    locale: pl,
  },
  pt: {
    name: 'Português',
    locale: pt,
  },
  da: {
    name: 'Dansk',
    locale: da,
  },
  sv: {
    name: 'Svenska',
    locale: sv,
  },
  fi: {
    name: 'Suomi',
    locale: fi,
  },
  tr: {
    name: 'Türkçe',
    locale: tr,
  },
  no: {
    name: 'Norsk',
    locale: nb,
  },
} as const;

const codes = Object.keys(languages);

export const translationVersion =
  import.meta.env.VITE_TRANSLATION_VERSION || __APP_VERSION__;

const i18nOptions: InitOptions = {
  showSupportNotice: false,
  supportedLngs: codes,
  debug: false,
  saveMissing: true,
  missingKeyHandler: (lngs, ns, key, fallbackValue) => {
    // Handle the missing key, for example, log it to the console
    if (key === 'homepage') return; // ignore this key, it is included but throws a warning

    if (import.meta.env.VITE_HIDE_MISSING_TRANSLATION_WARNING !== 'true') {
      console.warn(
        `Missing translation for key: "${key}" in namespace: "${ns}" for languages: "${lngs.join(
          ', ',
        )}". Fallback value: "${fallbackValue}"`,
      );
    }
  },
  interpolation: {
    escapeValue: false, // not needed for react as it escapes by default
  },
  react: {
    useSuspense: false,
    bindI18n: I18N_EVENTS.join(' '),
  },
  backend: {
    loadPath:
      api.i18n +
      '?namespace={{ns}}&locale={{lng}}&version=' +
      translationVersion,
  },
  load: 'languageOnly', // only load main language code, not country specific
};

void i18next
  // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
  // learn more: https://github.com/i18next/i18next-http-backend
  .use(HttpBackend)
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init(i18nOptions);

export default i18next;
